import { createSlice } from '@reduxjs/toolkit';
import { PLAN_TYPE } from '@/const/plans';

import * as thunks from './thunks';
import {
	handleAsyncThunkActionState,
	initialAsyncState,
	isAsyncThunkActionWithPrefix
} from '../utils';

import { STEPS } from './constants';

export { STEPS };

const initialState = {
	prices: [],
	priceId: null,
	step: STEPS.BILLING,
	initializing: false,
	vatVisible: false,
	fetchingPrices: false,
	relevantBillingData: {
		country: '',
		accountType: ''
	},
	nextAction: null,
	userCountry: null,
	fixedErrorCode: null,
	isPaymentBreakdownModalOpen: false,
	fetchPrices: initialAsyncState,
	plansModalOpen: false,
	selectedPlan: null
};

const checkoutSlice = createSlice(
	{
		name: 'checkout',
		initialState,
		reducers: {
			setPlan(state, action) {
				state.priceId = action.payload;
			},
			setPrice(state, action) {
				const { id, paymentHandlerId } = action.payload;
				state.priceId = id || null;
				state.paymentHandlerId = paymentHandlerId || null;
			},
			setSelectedPlan(state, action) {
				state.selectedPlan = action.payload;
			},
			setStep(state, action) {
				state.step = action.payload;
			},
			setPlansModalOpen(state, action) {
				state.plansModalOpen = action.payload;
			},
			setRelevantBillingData(state, action) {
				state.relevantBillingData = {
					...state.relevantBillingData,
					...action.payload
				};
			},
			setIsPaymentBreakdownModalOpen(state, action) {
				state.isPaymentBreakdownModalOpen = action.payload;
			},
			clearData(state) {
				Object.assign(state, initialState);
			}
		},
		extraReducers(builder) {
			builder.addCase(thunks.changePlan.fulfilled, (state, action) => {
				const { nextAction, priceId } = action.payload;

				state.nextAction = nextAction;
				state.priceId = priceId;
			});
			builder.addCase(thunks.fetchPrices.fulfilled, (state, action) => {
				state.prices = action.payload.prices.filter(
					p =>
						p.active &&
						![PLAN_TYPE.SERVICE, PLAN_TYPE.STORAGE].includes(p.name)
				);
				state.pricesReady = true;
			});
			builder.addCase(thunks.getPrices.fulfilled, (state, action) => {
				const { userCountry, prices, priceCurrency } = action.payload;

				if (userCountry) {
					state.userCountry = userCountry;
				}

				if (priceCurrency) {
					state.priceCurrency = priceCurrency;
				}

				state.prices = prices;
			});
			builder.addCase(thunks.initCheckout.fulfilled, (state, action) => {
				const {
					priceId,
					nextAction,
					userCountry,
					activePrices,
					step,
					relevantBillingData = {},
					paymentHandlerId,
					priceCurrency
				} = action.payload;

				if (userCountry) {
					state.userCountry = userCountry;
				}

				if (priceId) {
					state.priceId = priceId;
				}

				if (paymentHandlerId) {
					state.paymentHandlerId = paymentHandlerId;
				}

				if (priceCurrency) {
					state.priceCurrency = priceCurrency;
				}

				if (relevantBillingData) {
					state.relevantBillingData = {
						...(state?.relevantBillingData || {}),
						...relevantBillingData
					};
				}

				state.nextAction = nextAction;
				state.prices = activePrices;
				state.step = step;
			});

			builder.addMatcher(
				isAsyncThunkActionWithPrefix('checkout'),
				handleAsyncThunkActionState
			);
		}
	},
	initialState
);

export const {
	setStep,
	setPlan,
	setPrice,
	clearData,
	setRelevantBillingData,
	setIsPaymentBreakdownModalOpen,
	setPlansModalOpen,
	setSelectedPlan
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
